/**
 * agent-activity-store — central client-side reducer for the Live
 * Operations stream.
 *
 * One module-level store shared by every surface (LiveAISessionRail,
 * /app/live, ambient markers). useBrowserBus feeds it from the
 * agent_session / agent_activity SSE events plus durable replay; legacy
 * tool_activity envelopes are synthesized into an ephemeral session so old
 * servers still render something coherent.
 *
 * Consumers subscribe via useSyncExternalStore-compatible primitives:
 *   subscribe(listener)           → unsubscribe
 *   getSessions(workspaceId?)     → AgentSessionState[] newest-first
 *   getSession(id)                → single session
 *   replayWorkspace(wsId)         → pull missed events via the cursor API
 */
import type {
  LiveAgentActivity,
  LiveAgentSession,
  LiveToolActivity,
} from "./live-ai-session-events.js";

export interface AgentSessionState {
  session_id: string;
  workspace_id: string | null;
  source: string;
  mediator: string;
  status: string;
  title: string;
  current_step?: string;
  progress_current?: number;
  progress_total?: number;
  safe_summary?: string;
  error_code?: string;
  started_at: string;
  heartbeat_at: string;
  completed_at?: string;
  events: LiveAgentActivity[];
  /** Synthetic sessions from legacy tool_activity have no durable row. */
  ephemeral?: boolean;
}

const MAX_EVENTS_PER_SESSION = 200;
const MAX_SESSIONS = 60;
const SESSION_PRUNE_AFTER_MS = 30 * 60 * 1000;

const sessions = new Map<string, AgentSessionState>();
const seenEventIds = new Set<string>();
const listeners = new Set<() => void>();
let snapshotVersion = 0;

function notify(): void {
  snapshotVersion++;
  for (const listener of listeners) {
    try { listener(); } catch { /* consumer errors must not break the feed */ }
  }
}

export function subscribeAgentActivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function agentActivityVersion(): number {
  return snapshotVersion;
}

function ensureSession(event: LiveAgentActivity | {
  session_id: string;
  workspace_id?: string | null;
  source: string;
  mediator: string;
  heartbeat_at: string;
}): AgentSessionState {
  const existing = sessions.get(event.session_id);
  if (existing) return existing;
  const created: AgentSessionState = {
    session_id: event.session_id,
    workspace_id: event.workspace_id ?? null,
    source: event.source,
    mediator: event.mediator,
    status: "running",
    title: "Agent session",
    started_at: "heartbeat_at" in event ? event.heartbeat_at : new Date().toISOString(),
    heartbeat_at: "heartbeat_at" in event ? event.heartbeat_at : new Date().toISOString(),
    events: [],
  };
  sessions.set(event.session_id, created);
  return created;
}

function prune(): void {
  const now = Date.now();
  for (const [id, s] of sessions) {
    const terminal = s.status === "completed" || s.status === "failed" || s.status === "interrupted";
    if (terminal && now - Date.parse(s.heartbeat_at) > SESSION_PRUNE_AFTER_MS) {
      sessions.delete(id);
    }
  }
  // Hard bound — drop the oldest completed sessions first.
  if (sessions.size > MAX_SESSIONS) {
    const sorted = Array.from(sessions.values())
      .sort((a, b) => Date.parse(a.heartbeat_at) - Date.parse(b.heartbeat_at));
    for (const s of sorted) {
      if (sessions.size <= MAX_SESSIONS) break;
      if (s.status === "running" || s.status === "waiting" || s.status === "queued") continue;
      sessions.delete(s.session_id);
    }
  }
}

export function ingestAgentSession(payload: LiveAgentSession): void {
  const s = ensureSession(payload);
  if (payload.title) s.title = payload.title;
  if (payload.status) s.status = payload.status;
  if (payload.current_step !== undefined) s.current_step = payload.current_step;
  if (payload.progress_current !== undefined) s.progress_current = payload.progress_current;
  if (payload.progress_total !== undefined) s.progress_total = payload.progress_total;
  if (payload.safe_summary !== undefined) s.safe_summary = payload.safe_summary;
  if (payload.error_code !== undefined) s.error_code = payload.error_code;
  if (payload.completed_at) s.completed_at = payload.completed_at;
  s.heartbeat_at = payload.heartbeat_at ?? s.heartbeat_at;
  s.workspace_id = payload.workspace_id ?? s.workspace_id;
  notify();
}

export function ingestAgentActivity(event: LiveAgentActivity): void {
  if (!event?.session_id || !event.event_id) return;
  if (seenEventIds.has(event.event_id)) return; // relay + outbox may both deliver
  seenEventIds.add(event.event_id);
  if (seenEventIds.size > 20_000) {
    // Bound memory — keep the most recent half.
    const keep = Array.from(seenEventIds).slice(-10_000);
    seenEventIds.clear();
    for (const id of keep) seenEventIds.add(id);
  }

  const s = ensureSession(event);
  s.heartbeat_at = event.emitted_at > s.heartbeat_at ? event.emitted_at : s.heartbeat_at;
  if (event.workspace_id) s.workspace_id = event.workspace_id;

  // Session lifecycle kinds update the session row itself.
  if (event.kind === "session_completed") { s.status = "completed"; s.completed_at = event.emitted_at; }
  else if (event.kind === "session_failed") { s.status = "failed"; s.completed_at = event.emitted_at; s.error_code = event.error_code ?? s.error_code; }
  else if (event.kind === "session_interrupted") { s.status = "interrupted"; s.completed_at = event.emitted_at; }
  else if (event.kind === "session_waiting") s.status = "waiting";
  else if (event.kind === "session_paused") s.status = "paused";
  else if (event.kind === "session_started") s.status = "running";

  if (event.kind === "tool_started") s.current_step = event.title;
  if (event.progress_current != null) {
    s.progress_current = event.progress_current;
    s.progress_total = event.progress_total ?? s.progress_total;
  }
  if (event.title && s.title === "Agent session" && event.kind === "session_started") {
    s.title = event.title;
  }

  s.events.push(event);
  if (s.events.length > MAX_EVENTS_PER_SESSION) {
    s.events.splice(0, s.events.length - MAX_EVENTS_PER_SESSION);
  }
  prune();
  notify();
}

/**
 * Bridge for the legacy tool_activity envelope (pre-ledger servers and the
 * compatibility broadcast). Each burst maps onto an ephemeral session so
 * the grouped UI still has a container.
 */
export function ingestLegacyToolActivity(activity: LiveToolActivity): void {
  const sessionId = `legacy:${activity.mediator}:${activity.workspace_id ?? "acct"}`;
  const event: LiveAgentActivity = {
    event_id: `legacy:${activity.activity_id}:${activity.phase}`,
    session_id: sessionId,
    session_seq: 0,
    workspace_id: activity.workspace_id ?? null,
    kind: activity.phase === "started" ? "tool_started"
      : activity.phase === "succeeded" ? "tool_succeeded" : "tool_failed",
    phase: activity.phase === "started" ? "running" : activity.phase,
    source: "external_mcp",
    mediator: activity.mediator,
    tool_name: activity.tool_name,
    activity_id: activity.activity_id,
    title: `${activity.phase === "started" ? "Running" : activity.phase === "succeeded" ? "Finished" : "Failed"} ${activity.tool_name.replace(/_/g, " ")}`,
    duration_ms: activity.duration_ms,
    error_code: activity.error_code,
    emitted_at: activity.completed_at ?? activity.started_at,
  };
  const s = sessions.get(sessionId) ?? ensureSession({
    session_id: sessionId,
    workspace_id: activity.workspace_id ?? null,
    source: "external_mcp",
    mediator: activity.mediator,
    heartbeat_at: activity.started_at,
  });
  s.ephemeral = true;
  s.title = "External client activity";
  ingestAgentActivity(event);
}

// ── Reads ──────────────────────────────────────────────────────────────────
export function getAgentSessions(workspaceId?: string | null): AgentSessionState[] {
  const out: AgentSessionState[] = [];
  for (const s of sessions.values()) {
    if (workspaceId && s.workspace_id && s.workspace_id !== workspaceId) continue;
    out.push(s);
  }
  return out.sort((a, b) => Date.parse(b.heartbeat_at) - Date.parse(a.heartbeat_at));
}

export function getAgentSession(sessionId: string): AgentSessionState | null {
  return sessions.get(sessionId) ?? null;
}

// ── Replay ─────────────────────────────────────────────────────────────────
const cursorKey = (workspaceId: string) => `cmcp:agent-activity-cursor:${workspaceId}`;

function advanceAgentCursor(workspaceId: string, eventId?: string): void {
  if (!eventId || !/^\d+$/.test(eventId)) return;
  const key = cursorKey(workspaceId);
  const current = sessionStorage.getItem(key) ?? "0";
  if (!/^\d+$/.test(current) || BigInt(eventId) > BigInt(current)) {
    sessionStorage.setItem(key, eventId);
  }
}

/** Advance the durable cursor when a live event lands. */
export function noteLiveAgentEvent(workspaceId: string | null | undefined, eventId?: string): void {
  if (workspaceId) advanceAgentCursor(workspaceId, eventId);
}

interface ReplayPage {
  events?: Array<LiveAgentActivity & { id?: number }>;
  next_cursor?: string;
  has_more?: boolean;
}

/**
 * Pull missed events after the saved cursor. Bounded to 5 pages per pass —
 * the saved cursor persists, so a longer backlog resumes on next reconnect.
 */
export async function replayAgentActivity(workspaceId: string | null): Promise<void> {
  const key = workspaceId ? cursorKey(workspaceId) : null;
  let after = key ? sessionStorage.getItem(key) ?? "0" : "0";
  if (!/^\d+$/.test(after)) after = "0";
  const base = workspaceId
    ? `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/agent-activity`
    : `/api/v1/agent-activity`;
  for (let page = 0; page < 5; page++) {
    const response = await fetch(`${base}?after=${encodeURIComponent(after)}&limit=200`, {
      credentials: "include",
    });
    if (!response.ok) throw new Error(`agent replay failed (${response.status})`);
    const body = await response.json() as ReplayPage;
    let progressed = false;
    for (const ev of body.events ?? []) {
      const before = seenEventIds.size;
      ingestAgentActivity(ev);
      progressed = progressed || seenEventIds.size !== before;
    }
    if (body.next_cursor && /^\d+$/.test(body.next_cursor)) {
      after = body.next_cursor;
      if (workspaceId) advanceAgentCursor(workspaceId, after);
    }
    if (!body.has_more || (body.events ?? []).length === 0) return;
  }
}
